// New training session (Session AFL): a four-step flow so the office records one on-site training
// session in the order it actually happened. Where and what (step 1), who was there (step 2), the
// notes (step 3), then a review before anything is written (step 4).
//
// Nothing is saved until the final confirm: Back works at every step and a half-filled form costs
// nothing. The lift picker is optional on purpose, because plenty of training (a fire evacuation
// briefing, say) is about the building rather than one lift.
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { RecordPicker } from "../../components/RecordPicker";
import { assets as assetsResource } from "../../lib/resources";
import type { Asset } from "../../lib/resources";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { Button } from "../../components/Button";
import { TRAINING_TYPES, TRAINING_TYPE_LABELS, trainingTypeLabel } from "../../lib/trainingTypes";
import type { TrainingAttendeeInput, TrainingType } from "../../lib/trainingTypes";
import { TrainingReferenceIndicator } from "./TrainingReferenceIndicator";
import { AssignEngineerField } from "./AssignEngineerField";

const inputCls =
  "w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";
const labelCls = "mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body";
const ghostBtn =
  "rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green disabled:opacity-50";

type Step = 1 | 2 | 3 | 4;

const STEP_TITLES: Record<Step, string> = {
  1: "Where and what",
  2: "Who attended",
  3: "Notes",
  4: "Review and confirm",
};

// A blank attendee row. Kept as its own factory so "+ Add another attendee" and the initial row
// cannot drift apart.
const blankAttendee = (): TrainingAttendeeInput => ({ name: "", role: "", email: "" });

function eleRef(a: Asset): string {
  return a.eleNumber ?? a.legacyEleNumber ?? "Not allocated";
}

export function NewTrainingSessionModal({
  open,
  onClose,
  onCreated,
  lockedSiteId,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the new session's id once it is saved (the page navigates to its detail page). */
  onCreated: (id: string) => void;
  /** Pre-selects a site (opened from that site's page). The picker still lets the office change it. */
  lockedSiteId?: string | null;
}) {
  const [step, setStep] = useState<Step>(1);
  const [siteId, setSiteId] = useState("");
  // The chosen site's name, kept from the pick (Session SC2A: the sites are searched, not preloaded).
  const [siteName, setSiteName] = useState("");
  const [trainingType, setTrainingType] = useState<TrainingType>("HAND_WINDING");
  const [customLabel, setCustomLabel] = useState("");
  const [sessionDate, setSessionDate] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [deliveredBy, setDeliveredBy] = useState("");
  // Session AGG: whose phone this session appears on. "" = unassigned (any engineer at the site).
  const [assignedEngineerId, setAssignedEngineerId] = useState("");
  // Kept alongside the id purely so the review step can NAME the engineer (Session AGM). Never sent.
  const [assignedEngineerName, setAssignedEngineerName] = useState<string | null>(null);
  const [assetIds, setAssetIds] = useState<string[]>([]);
  const [attendees, setAttendees] = useState<TrainingAttendeeInput[]>([blankAttendee()]);
  const [notes, setNotes] = useState("");
  const [customerNotes, setCustomerNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [siteLifts, setSiteLifts] = useState<Asset[]>([]);
  const [liftsLoaded, setLiftsLoaded] = useState(false);
  const [liftsError, setLiftsError] = useState(false);

  // Reset to a clean first step every time the modal opens, so a cancelled entry never leaks into
  // the next one.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setSiteId(lockedSiteId ?? "");
    setSiteName("");
    setTrainingType("HAND_WINDING");
    setCustomLabel("");
    setSessionDate("");
    setDurationMinutes("");
    setDeliveredBy("");
    // Session AGM: this was missing, so an engineer picked in a cancelled entry survived into the
    // next New training session and could be booked onto a different site without being re-picked.
    setAssignedEngineerId("");
    setAssignedEngineerName(null);
    setAssetIds([]);
    setAttendees([blankAttendee()]);
    setNotes("");
    setCustomerNotes("");
    setError(null);
    setSaving(false);
  }, [open, lockedSiteId]);

  const loadLifts = useCallback(() => {
    if (!siteId) {
      setSiteLifts([]);
      setLiftsLoaded(false);
      setLiftsError(false);
      return;
    }
    setLiftsLoaded(false);
    setLiftsError(false);
    assetsResource
      .list({ siteId })
      .then((rows) => {
        setSiteLifts(rows.filter((a) => (a.assetClass ?? "lift") === "lift"));
        setLiftsLoaded(true);
      })
      .catch(() => {
        setLiftsLoaded(true);
        setLiftsError(true);
      });
  }, [siteId]);

  useEffect(() => {
    if (!open) return;
    loadLifts();
  }, [open, loadLifts]);

  // Changing the site invalidates any lift already ticked (a lift belongs to one site, and the
  // server refuses a cross-site link).
  useEffect(() => {
    setAssetIds([]);
  }, [siteId]);

  const namedAttendees = useMemo(() => attendees.filter((a) => a.name.trim().length > 0), [attendees]);
  const chosenLifts = useMemo(() => siteLifts.filter((l) => assetIds.includes(l.id)), [siteLifts, assetIds]);

  const step1Complete = siteId.trim().length > 0 && sessionDate.trim().length > 0;

  if (!open) return null;

  function toggleLift(id: string) {
    setAssetIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function setAttendee(index: number, patch: Partial<TrainingAttendeeInput>) {
    setAttendees((prev) => prev.map((a, i) => (i === index ? { ...a, ...patch } : a)));
  }

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const created = await api.training.create({
        siteId,
        trainingType,
        customLabel: trainingType === "OTHER" && customLabel.trim() ? customLabel.trim() : null,
        sessionDate,
        durationMinutes: durationMinutes.trim() ? Number(durationMinutes) : null,
        deliveredBy: deliveredBy.trim() || null,
        assignedEngineerId: assignedEngineerId || null,
        notes: notes.trim() || null,
        customerNotes: customerNotes.trim() || null,
        assetIds,
        attendees: namedAttendees.map((a) => ({
          name: a.name.trim(),
          role: (a.role ?? "").trim() || null,
          email: (a.email ?? "").trim() || null,
        })),
      });
      onCreated(created.id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message || e.code : "Could not save the training session. Please try again.");
      setSaving(false);
    }
  }

  return (
    <ModalShell
      ariaLabel="New training session"
      onClose={() => !saving && onClose()}
      width="max-w-lg"
      panelClassName="flex max-h-[88vh] flex-col overflow-y-auto p-5"
    >
      <h2 className="font-heading text-xl text-ele-strong">New training session</h2>
      <p className="mb-4 text-xs text-ele-body">
        Step {step} of 4: {STEP_TITLES[step]}
      </p>

      {step === 1 && (
        <div className="space-y-3">
          <div>
            <RecordPicker
              kind="site"
              id="training-site"
              label="Site"
              value={siteId}
              placeholder="Search for a site"
              // A site chosen before the modal opened (the site page links in) has no pick to take its name from: the picker reads it.
              onLabel={setSiteName}
              onChange={(id, rec) => {
                setSiteId(id);
                setSiteName(rec?.label ?? "");
              }}
            />
          </div>

          <div>
            <label className={labelCls} htmlFor="training-type">
              Training type
            </label>
            <select
              id="training-type"
              value={trainingType}
              onChange={(e) => setTrainingType(e.target.value as TrainingType)}
              className={inputCls}
            >
              {TRAINING_TYPES.map((t) => (
                <option key={t} value={t}>
                  {TRAINING_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
            {/* Session AFP: whether a customer-facing reference document is on file for this type, so
                the office can check the material exists before booking. Links to the library. */}
            <TrainingReferenceIndicator trainingType={trainingType} className="mt-2" />
          </div>

          {trainingType === "OTHER" && (
            <div>
              <label className={labelCls} htmlFor="training-custom-label">
                What was the training?
              </label>
              <input
                id="training-custom-label"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="For example: Machine room access briefing"
                className={inputCls}
              />
            </div>
          )}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls} htmlFor="training-date">
                Date
              </label>
              <input id="training-date" type="date" value={sessionDate} onChange={(e) => setSessionDate(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} htmlFor="training-duration">
                Duration in minutes (optional)
              </label>
              <input
                id="training-duration"
                type="number"
                min={1}
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(e.target.value)}
                onFocus={(e) => e.currentTarget.select()}
                className={inputCls}
              />
            </div>
          </div>

          <div>
            <label className={labelCls} htmlFor="training-delivered-by">
              Delivered by (internal, never shown to the customer)
            </label>
            <input id="training-delivered-by" value={deliveredBy} onChange={(e) => setDeliveredBy(e.target.value)} className={inputCls} />
          </div>

          <AssignEngineerField
            value={assignedEngineerId}
            onChange={(userId, name) => {
              setAssignedEngineerId(userId);
              setAssignedEngineerName(name);
            }}
            labelClassName={labelCls}
            selectClassName={inputCls}
          />

          <fieldset>
            <legend className={labelCls}>Lifts covered (optional)</legend>
            {!siteId ? (
              <p className="text-sm text-ele-body">Choose a site to list its lifts.</p>
            ) : liftsError ? (
              <p className="text-sm text-ele-danger">
                Could not load this site&apos;s lifts.{" "}
                <button type="button" onClick={loadLifts} className="text-ele-green underline">
                  Retry
                </button>
              </p>
            ) : !liftsLoaded ? (
              <p className="text-sm text-ele-body">Loading lifts...</p>
            ) : siteLifts.length === 0 ? (
              <p className="text-sm text-ele-body">No lifts are on file for this site.</p>
            ) : (
              <div className="space-y-1">
                {siteLifts.map((l) => (
                  <label key={l.id} className="flex items-center gap-2 text-sm text-ele-strong">
                    <input type="checkbox" checked={assetIds.includes(l.id)} onChange={() => toggleLift(l.id)} />
                    <span className="ele-ref">{eleRef(l)}</span>
                    <span className="text-ele-body">{l.liftType ?? ""}</span>
                  </label>
                ))}
                <p className="text-xs text-ele-body">Leave every lift unticked for training about the building rather than one lift.</p>
              </div>
            )}
          </fieldset>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <p className="text-sm text-ele-body">
            Everyone who attended. Their names appear on the certificate, so enter them as they should read.
          </p>
          {attendees.map((a, i) => (
            <div key={i} className="rounded-ele border border-ele-purple-accent p-3">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <label className={labelCls} htmlFor={`attendee-name-${i}`}>
                    Name
                  </label>
                  <input id={`attendee-name-${i}`} value={a.name} onChange={(e) => setAttendee(i, { name: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls} htmlFor={`attendee-role-${i}`}>
                    Role (optional)
                  </label>
                  <input
                    id={`attendee-role-${i}`}
                    value={a.role ?? ""}
                    onChange={(e) => setAttendee(i, { role: e.target.value })}
                    placeholder="For example: Building manager"
                    className={inputCls}
                  />
                </div>
              </div>
              <div className="mt-3">
                <label className={labelCls} htmlFor={`attendee-email-${i}`}>
                  Email (optional)
                </label>
                <input id={`attendee-email-${i}`} value={a.email ?? ""} onChange={(e) => setAttendee(i, { email: e.target.value })} className={inputCls} />
              </div>
              {attendees.length > 1 && (
                <button
                  type="button"
                  onClick={() => setAttendees((prev) => prev.filter((_, x) => x !== i))}
                  className="mt-2 font-menu text-xs tracking-wide text-ele-danger max-sm:inline-flex max-sm:min-h-11 max-sm:items-center"
                >
                  Remove this attendee
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={() => setAttendees((prev) => [...prev, blankAttendee()])}
            className={`${ghostBtn} max-sm:w-full max-sm:min-h-11`}
          >
            + Add another attendee
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <div>
            <label className={labelCls} htmlFor="training-notes">
              Office notes: not shown to customer
            </label>
            <textarea id="training-notes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls} htmlFor="training-customer-notes">
              Customer-facing notes: visible in portal
            </label>
            <textarea
              id="training-customer-notes"
              rows={4}
              value={customerNotes}
              onChange={(e) => setCustomerNotes(e.target.value)}
              className={inputCls}
            />
            <p className="mt-1 text-xs text-ele-body">These notes also print on the certificate.</p>
          </div>
        </div>
      )}

      {step === 4 && (
        <dl className="space-y-2 text-sm">
          <Review label="Site" value={siteName || "Not chosen"} />
          <Review label="Training" value={trainingTypeLabel(trainingType, customLabel)} />
          <Review label="Date" value={sessionDate || "Not chosen"} />
          <Review label="Duration" value={durationMinutes.trim() ? `${durationMinutes} minutes` : "Not recorded"} />
          <Review label="Delivered by" value={deliveredBy.trim() || "Not recorded"} />
          <Review
            label="Assigned to"
            value={assignedEngineerId ? (assignedEngineerName ?? "One engineer") : "Any engineer"}
          />
          <Review
            label="Lifts covered"
            value={chosenLifts.length === 0 ? "None: general training" : chosenLifts.map((l) => eleRef(l)).join(", ")}
          />
          <Review
            label="Attendees"
            value={
              namedAttendees.length === 0
                ? "None recorded"
                : namedAttendees.map((a) => (a.role && a.role.trim() ? `${a.name.trim()} (${a.role.trim()})` : a.name.trim())).join(", ")
            }
          />
          <Review label="Office notes" value={notes.trim() || "None"} />
          <Review label="Customer notes" value={customerNotes.trim() || "None"} />
          <p className="pt-1 text-xs text-ele-body">
            Saving records the session as scheduled. The certificate is issued later, when you mark it as completed.
          </p>
        </dl>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-ele border border-ele-danger px-3 py-2 text-sm text-ele-strong">
          {error}
        </p>
      )}

      <div className={`mt-5 gap-2 ${MODAL_ACTIONS}`}>
        <button type="button" onClick={onClose} disabled={saving} className={`${ghostBtn} ${MODAL_ACTION_BTN}`}>
          Cancel
        </button>
        {step > 1 && (
          <button type="button" onClick={() => setStep((s) => (s - 1) as Step)} disabled={saving} className={`${ghostBtn} ${MODAL_ACTION_BTN}`}>
            Back
          </button>
        )}
        {step < 4 ? (
          <Button
            onClick={() => setStep((s) => (s + 1) as Step)}
            disabled={step === 1 && !step1Complete}
            className={MODAL_ACTION_BTN}
          >
            Next
          </Button>
        ) : (
          <Button onClick={submit} disabled={saving || !step1Complete} className={MODAL_ACTION_BTN}>
            {saving ? "Saving..." : "Save training session"}
          </Button>
        )}
      </div>
      {step === 1 && !step1Complete && (
        <p className="mt-2 text-right text-xs text-ele-body">Choose a site and a date to continue.</p>
      )}
    </ModalShell>
  );
}

function Review({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-3">
      <dt className="font-menu text-xs uppercase tracking-wide text-ele-body sm:w-40 sm:shrink-0">{label}</dt>
      <dd className="min-w-0 break-words text-ele-strong">{value}</dd>
    </div>
  );
}
